import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import classNames from "classnames"
import { v4 as uuidv4 } from "uuid"

import { useLazyGetInventoryQuery } from "store/api/widgetsApi"
import { useAppDispatch } from "store/hooks"
import { showNewOrderModal } from "store/modalSlise"

import { WidgetLayout } from "layouts/WidgetLayout"
import { BarChart } from "components/common/BarChart"
import { Tooltip } from "components/common/Tooltip"

import styles from "./index.module.scss"

const initialState = {
  sufficientDays: 0,
  inventoryDevices: 0,
  orderedDevices: 0,
}

interface Props {
  updateIndex: number
}

export const WidgetInventory = ({ updateIndex }: Props) => {
  const [triggerGetInventory, { isFetching }] = useLazyGetInventoryQuery()
  const [widgetData, setWidgetData] = useState(initialState)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const onNewClick = () => {
    dispatch(showNewOrderModal(true))
    navigate("/orders")
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await triggerGetInventory()
        if (res?.data) {
          setWidgetData({
            sufficientDays: res?.data?.data?.sufficient_days,
            inventoryDevices: res?.data?.data?.inventory_devices,
            orderedDevices: res?.data?.data?.ordered_devices,
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [updateIndex])

  return (
    <WidgetLayout
      className={styles.widgetInventory}
      title="Inventory"
      buttonText="New order"
      onButtonClick={onNewClick}
    >
      <div
        className={classNames(styles.widgetContent, {
          [styles.disabled]: isFetching,
        })}
      >
        <BarChart
          items={[
            {
              value: widgetData.orderedDevices,
              color: "#3B82F6",
            },
            {
              value: widgetData.inventoryDevices,
              color: "#BFDBFE",
            },
          ]}
        />
        <div className={styles.widgetMainValue}>
          <Tooltip id={uuidv4()} text="SUFFICIENT FOR">
            This indicator gauges sufficiency by averaging Nuve thermostats daily installations over the past 14 days.
            If, for example, the average rate is 10 and there are 150 devices in stock, the widget shows &#39;Sufficient
            for 15 days,&#39; indicating the estimated stock duration based on historical rates.
          </Tooltip>
          <div className={styles.widgetValueWrapper}>
            {widgetData.sufficientDays}
            <span className={styles.valueNote}> Days</span>
          </div>
        </div>
        <footer className={styles.widgetFooter}>
          <div className={styles.widgetFooterItem}>
            <Tooltip id={uuidv4()} text="In Inventory">
              Number of available devices in storage
            </Tooltip>
            <div className={styles.widgetValueWrapper}>{widgetData.inventoryDevices}</div>
          </div>
          <div className={styles.widgetFooterItem}>
            <Tooltip id={uuidv4()} text="Ordered">
              Number of ordered devices{" "}
            </Tooltip>
            <div className={styles.widgetValueWrapper}>{widgetData.orderedDevices}</div>
          </div>
        </footer>
      </div>
    </WidgetLayout>
  )
}
